'use client';
import styled, { css } from 'styled-components';
import colors from './colors';
import gradients from './gradients';

const oxenBorderGradient = `linear-gradient(90deg,${gradients.brandLoop.join(',')})`;
const oxenBorderImageGradient = `linear-gradient(90deg,${gradients.vibrantBrand.join(',')}) 1`;

const gradientBorder = ({
  borderRadius = 5,
  backgroundColor = colors.primary.white,
}: {
  borderRadius?: number;
  backgroundColor?: string;
}) => css`
  position: relative;
  z-index: 1;
  border-radius: ${borderRadius}px;
  background: ${oxenBorderGradient};
  background-clip: padding-box;

  &:after {
    position: absolute;
    top: 1px;
    bottom: 1px;
    left: 1px;
    right: 1px;
    background: ${backgroundColor};
    content: '';
    z-index: -1;
    border-radius: ${borderRadius}px;
  }
`;

type TGlowParams = { size?: CSSUnitValue; opacity?: number };

const animatedGradientBorder = ({
  borderRadius = 5,
  borderWidth = 2,
  glow,
}: {
  borderRadius?: number;
  borderWidth?: number;
  glow?: TGlowParams;
}) => {
  const outerBorderRadius = `${borderRadius + borderWidth}px`;
  const innerBorderRadius = `${borderRadius}px`;
  const borderInset = `-${borderWidth}px`;
  return css`
    --border-angle: 0turn;
    border-radius: ${outerBorderRadius};
    z-index: 0;

    & > * {
      border-radius: ${innerBorderRadius};
      border: none;
    }

    &::before,
    &::after {
      content: '';
      position: absolute;
      inset: ${borderInset};
      z-index: -1;

      background: conic-gradient(from var(--border-angle), ${gradients.brandLoop.join(',')});
      border-radius: inherit;
      background-position: center center;
      background-repeat: no-repeat;
      animation: bg-spin 2s linear infinite;
    }

    &:after {
      filter: ${glow ? `blur(${glow.size || '2rem'})` : 'none'};
      opacity: ${glow ? glow.opacity || 0.5 : 0};
    }

    @keyframes bg-spin {
      to {
        --border-angle: 1turn;
      }
    }
    &:hover {
      animation-play-state: paused;
    }
    @property --border-angle {
      syntax: '<angle>';
      inherits: true;
      initial-value: 0turn;
    }
  `;
};

const gradientText = () => css`
  background: linear-gradient(to right, ${gradients.vibrantBrand.join(',')});
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const animatedGradientText = () => css`
  --speed: 6s;
  background: linear-gradient(to right, ${gradients.vibrantBrand.join(',')});
  background-size: 200% auto;

  color: #000;
  background-clip: text;
  /* text-fill-color: transparent; */
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  animation: shine var(--speed) linear infinite;
  @keyframes shine {
    to {
      background-position: 200% center;
    }
  }
`;

const animatedGradientWrapper = ({
  glow = true,
  glowWidth = '2rem',
  glowOpacity = 0.5,
  borderWidth = 2,
  borderRadius = 5,
}: {
  glow?: boolean;
  glowWidth?: string;
  glowOpacity?: number;
  borderWidth?: number;
  borderRadius?: number;
}) => {
  const calculatedBorderRadius = `${borderRadius + borderWidth}px`;
  const borderInset = `-${borderWidth}px`;
  return css`
    position: relative;
    --border-angle: 0turn;
    /* --border-inset: calc(${borderInset}px); */

    &:not(.on) {
      z-index: 1;
    }

    &.on {
      border-radius: ${calculatedBorderRadius};
      z-index: 0;

      & > * {
        border-radius: ${calculatedBorderRadius};
        border: none;
      }

      &::before,
      &::after {
        content: '';
        position: absolute;
        inset: ${borderInset};
        z-index: -1;

        background: conic-gradient(from var(--border-angle), ${gradients.brand.join(',')});
        border-radius: inherit;
        background-position: center center;
        background-repeat: no-repeat;
        animation: bg-spin 2s linear infinite;
      }

      &:after {
        filter: ${glow ? `blur(${glowWidth})` : 'none'};
        opacity: ${glowOpacity};
      }

      @keyframes bg-spin {
        to {
          --border-angle: 1turn;
        }
      }
      &:hover {
        animation-play-state: paused;
      }
      @property --border-angle {
        syntax: '<angle>';
        inherits: true;
        initial-value: 0turn;
      }
    }
  `;
};

const checkedImageBackground = () => css`
  background-color: ${colors.secondary.lightGray1};
  /* opacity: 0.2; */
  background-image:
    repeating-linear-gradient(
      45deg,
      ${colors.secondary.lightGray2} 25%,
      transparent 25%,
      transparent 75%,
      ${colors.secondary.lightGray2} 75%,
      ${colors.secondary.lightGray2}
    ),
    repeating-linear-gradient(
      45deg,
      ${colors.secondary.lightGray2} 25%,
      ${colors.secondary.lightGray1} 25%,
      ${colors.secondary.lightGray1} 75%,
      ${colors.secondary.lightGray2} 75%,
      ${colors.secondary.lightGray2}
    );
  background-position:
    0 0,
    10px 10px;
  background-size: 20px 20px;
`;

const avoidAutofillHighlight = css`
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    filter: none; /* u need this for firefox */
    -webkit-box-shadow: 0 0 0px 40rem white inset;
  }
`;

const lib = Object.assign({
  gradientBorder,
  animatedGradientBorder,
  gradientText,
  animatedGradientText,
  animatedGradientWrapper,
  checkedImageBackground,
  avoidAutofillHighlight,
  oxenBorderImageGradient,
});

export default lib;
